import { useEffect } from 'react';
import { IMAGES, RING, assignRing } from '../data/inboundBusinessData';

/**
 * 背景のリングを構築して回す。
 *
 * wrap の外側（左右の余白）を通る大きな円に、正方形の写真を並べる。
 * 円は画面より大きく、左右で見切れる前提。
 *
 * 画像は回転させない。リングの回転をそれぞれの画像で打ち消し、常に水平に保つ。
 *
 * @param {React.RefObject<HTMLElement>} ringRef  写真を差し込む器
 * @param {React.RefObject<HTMLElement>} layerRef 円の中心を置く層
 * @param {React.RefObject<HTMLElement>} anchorRef 円の中心を合わせるセクション
 * @param {boolean} enabled 回転を有効にするか
 */
export default function useRingDecoration(ringRef, layerRef, anchorRef, enabled = true) {
  useEffect(() => {
    const ring = ringRef.current;
    const layer = layerRef.current;
    const anchor = anchorRef.current;
    if (!ring || !layer || !anchor) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const { slots, radius, size, cornerRatio } = RING;

    // 写真を配置する
    const plan = assignRing(slots, IMAGES.ring);
    const corner = Math.round(size * cornerRatio);
    const items = [];
    ring.textContent = '';

    plan.forEach((src, i) => {
      const slot = document.createElement('div');
      slot.className = 'ring-slot';

      const frame = document.createElement('div');
      frame.className = 'ring-frame';
      frame.style.width = `${size}px`;
      frame.style.height = `${size}px`;
      frame.style.marginLeft = `${-size / 2}px`;
      frame.style.marginTop = `${-size / 2}px`;
      frame.style.borderRadius = `${corner}px`;

      const img = document.createElement('img');
      img.src = src;
      img.alt = '';               // 装飾なので読み上げ対象から外す
      img.loading = 'lazy';
      img.decoding = 'async';

      frame.appendChild(img);
      slot.appendChild(frame);
      ring.appendChild(slot);
      items.push({ slot, frame, angle: (360 / slots) * i });
    });

    let baseRotation = 0;
    let scrollRotation = 0;
    let raf = null;
    let ticking = false;

    const layout = () => {
      const rotation = baseRotation + scrollRotation;
      ring.style.transform = `rotate(${rotation.toFixed(2)}deg)`;
      items.forEach(({ slot, frame, angle }) => {
        slot.style.transform = `rotate(${angle}deg) translateY(${-radius}px)`;
        // リングの回転を打ち消して常に水平に保つ
        frame.style.transform = `rotate(${(-(angle + rotation)).toFixed(2)}deg)`;
      });
    };

    const place = () => {
      const r = anchor.getBoundingClientRect();
      layer.style.top = `${r.top + window.scrollY + r.height / 2}px`;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        if (!enabled || reduce) return;
        const r = anchor.getBoundingClientRect();
        scrollRotation =
          ((window.innerHeight - r.top) / (window.innerHeight + r.height)) * 40;
        layout();
      });
    };

    const tick = () => {
      if (enabled && !reduce) {
        baseRotation += 0.03;
        layout();
      }
      raf = requestAnimationFrame(tick);
    };

    const onResize = () => { place(); layout(); };

    place();
    layout();
    onScroll();
    raf = requestAnimationFrame(tick);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      ring.textContent = '';
    };
  }, [ringRef, layerRef, anchorRef, enabled]);
}
